import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];

// Human byte size with binary scaling: "0 B", "812 KB", "4.7 GB". The unit names
// are the same across our locales, but the decimal separator is not — `lang`
// (optional) makes pt/es/it read "4,7 GB" where en reads "4.7 GB".
export function formatBytes(bytes, lang) {
  const n = Number(bytes) || 0;
  if (n <= 0) return `0 ${BYTE_UNITS[0]}`;
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Bytes and KB are precise enough whole; larger units need a decimal to be useful.
  const digits = unit <= 1 ? 0 : 1;
  const num = value.toLocaleString(lang || undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${num} ${BYTE_UNITS[unit]}`;
}

const BPS_UNITS = ["bps", "Kbps", "Mbps", "Gbps", "Tbps"];

// Human bitrate: "0 bps", "812 Kbps", "1.4 Gbps". Decimal (1000) scaling, not
// binary — network rates are quoted in powers of ten, unlike file sizes.
export function formatBps(bps, lang) {
  const n = Number(bps) || 0;
  if (n <= 0) return `0 ${BPS_UNITS[0]}`;
  let value = n;
  let unit = 0;
  while (value >= 1000 && unit < BPS_UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : 1;
  const num = value.toLocaleString(lang || undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${num} ${BPS_UNITS[unit]}`;
}

// Human, localized duration: "2 dias, 3 horas e 5 minutos". Days only appear
// once the span reaches a full day; hours + minutes are always shown. `t` is the
// i18n translator (uses the duration.* keys). Returns "" for a non-positive span.
export function formatDuration(totalMinutes, t) {
  const total = Math.max(0, Math.floor(Number(totalMinutes) || 0));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;
  const unit = (n, one, many) => `${n} ${t(n === 1 ? one : many)}`;
  const parts = [];
  if (days >= 1) parts.push(unit(days, "duration.day", "duration.days"));
  parts.push(unit(hours, "duration.hour", "duration.hours"));
  parts.push(unit(minutes, "duration.minute", "duration.minutes"));
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} ${t("duration.and")} ${parts[parts.length - 1]}`;
}
